import { ComponentProps } from "react";
import { useSetPageParam } from "@hooks/useSetPageParam";
import Card from "@particles/card/Card";
import { CardVariations } from "@particles/card/utils/types";
import { cn } from "@util/helpers";

type TemplateGCardProps = {
  /** This is the classname of the component. */
  className?: string;
  /** The card prop to be displayed. */
  cardProp: ComponentProps<typeof Card>;
  /** The index of the card to be displayed. */
  index: number;
};

/** This is the template G card component. */
const TemplateGCard = ({
  className,
  cardProp,
  index,
}: TemplateGCardProps): React.ReactElement => {
  const { ref } = useSetPageParam({ cardIndex: index });

  const variant: CardVariations = {
    kicker: {
      color: cn("text-gray-515"),
      extraClass: cn("font-poppins text-4xs font-light leading-6"),
    },
    image: {
      width: "w-1/4",
      position: "right",
      aspectRatio: "aspect-3x2",
      orientation: "landscape",
      filters: { w: 900, h: 600, dpr: 1, f: "webp" },
    },
    content: {
      width: "w-3/4",
      extraClass: "pr-4",
      blurb: { extraClass: "text-gray-850", wrapperClassName: cn("lg:my-2") },
      updatedTimeColor: cn("text-gray-850"),
      updatedTimeExtraClass: cn("font-poppins text-7xs"),
      updatedTimeWrapperClass: cn("mt-2 lg:mt-auto"),
    },
    title: {
      size: "text-base",
      color: "text-gray-850",
      extraClass: cn("font-bold font-lct"),
    },
    subscriberLabel: { extraClass: "mt-2 mb-0" },
  };

  return (
    <Card
      {...cardProp}
      variations={variant}
      className={cn(
        "relative lg:items-stretch",
        "after:absolute after:-bottom-4 after:h-[1px] after:w-full after:bg-gray-175",
        className
      )}
      ref={ref}
    />
  );
};

export default TemplateGCard;
